import { AlertCircle, LayoutGrid, Search, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { selectUser, useAuthStore } from '@/features/auth/auth.store';
import { normalizeSearch } from '@/lib/search';
import { cn } from '@/lib/utils';

import { CategoryCard } from '../components/category-card';
import { ModuleCard } from '../components/module-card';
import { PlanCard } from '../components/plan-card';
import { useModulosDisponibles } from '../hooks/use-modulos-disponibles';
import type { ModuloDisponible } from '../modulos.types';

interface ResultadoBusqueda {
  categoria: string;
  modulo: ModuloDisponible;
}

/**
 * Pantalla de inicio del panel: segmentación de módulos disponibles.
 *
 * Todo se resuelve con el catálogo del backend (categorías + módulos filtrados
 * por los grupos del usuario y por el plan contratado), por lo que refleja
 * exactamente lo que el usuario puede usar.
 */
export function HomePage() {
  useDocumentTitle('Módulos');

  const user = useAuthStore(selectUser);
  const { data, isPending, isError, refetch, isRefetching } = useModulosDisponibles();

  const [query, setQuery] = useState('');

  // `data?.categorias ?? []` crearía un array nuevo en cada render e invalidaría
  // el memo de búsqueda; se memoriza para mantener la referencia estable.
  const categorias = useMemo(() => data?.categorias ?? [], [data]);

  const resultados = useMemo<ResultadoBusqueda[] | null>(() => {
    const termino = normalizeSearch(query);
    if (termino.length < 2) return null;

    const encontrados: ResultadoBusqueda[] = [];

    for (const categoria of categorias) {
      for (const modulo of categoria.modulos) {
        const heno = [modulo.nombre, modulo.descripcion, categoria.nombre, modulo.codigo]
          .map(normalizeSearch)
          .join(' ');

        if (heno.includes(termino)) {
          encontrados.push({ categoria: categoria.nombre, modulo });
        }
      }
    }

    return encontrados;
  }, [categorias, query]);

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <header>
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
          Bienvenido nuevamente
        </p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink-900">
          {user?.displayName ?? 'Usuario'}
        </h1>
        <p className="mt-1 text-sm text-ink-500">
          Selecciona una categoría para ver los módulos que tienes habilitados.
        </p>
      </header>

      {/* Plan contratado */}
      <PlanCard
        plan={data?.plan ?? null}
        totalModulos={data?.total_modulos ?? 0}
        className="animate-fade-in"
      />

      {isError ? (
        <Alert variant="error" title="No se pudo cargar el catálogo de módulos">
          <div className="flex flex-wrap items-center gap-3">
            <span>Verifica la conexión con el servidor e inténtalo nuevamente.</span>
            <Button size="sm" variant="outline" onClick={() => void refetch()} loading={isRefetching}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {data?.aviso ? <Alert variant="warning">{data.aviso}</Alert> : null}

      {/* Buscador */}
      {!isError && (isPending || categorias.length > 0) ? (
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3.5 top-1/2 size-4.5 -translate-y-1/2 text-ink-400"
          />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar módulo… (ej. cuadres, kardex, tanques)"
            aria-label="Buscar módulo"
            className={cn(
              'h-12 w-full rounded-control border border-ink-200 bg-white pl-11 pr-4 text-sm text-ink-900',
              'placeholder:text-ink-400 transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none',
            )}
          />
        </div>
      ) : null}

      {/* Carga */}
      {isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-44 rounded-card" />
          ))}
        </div>
      ) : null}

      {/* Resultados de búsqueda */}
      {!isPending && resultados ? (
        resultados.length > 0 ? (
          <section aria-label="Resultados de búsqueda" className="space-y-3">
            <p className="text-sm text-ink-500">
              {resultados.length} {resultados.length === 1 ? 'resultado' : 'resultados'} para{' '}
              <strong className="font-semibold text-ink-800">“{query.trim()}”</strong>
            </p>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {resultados.map(({ categoria, modulo }) => (
                <ModuleCard key={modulo.codigo} modulo={modulo} categoria={categoria} />
              ))}
            </div>
          </section>
        ) : (
          <div className="rounded-card border border-dashed border-ink-200 bg-white/70 px-6 py-12 text-center">
            <Search className="mx-auto size-6 text-ink-300" aria-hidden="true" />
            <p className="mt-3 text-sm font-medium text-ink-700">Sin resultados</p>
            <p className="mt-1 text-xs text-ink-500">
              No hay módulos que coincidan con “{query.trim()}”. Verifica el nombre o solicita el
              acceso al administrador.
            </p>
          </div>
        )
      ) : null}

      {/* Categorías */}
      {!isPending && !resultados ? (
        categorias.length > 0 ? (
          <section aria-label="Categorías de módulos" className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <LayoutGrid className="size-4 text-ink-400" aria-hidden="true" />
              <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-400">
                Categorías disponibles
              </h2>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {categorias.map((categoria) => (
                <CategoryCard key={categoria.codigo} categoria={categoria} />
              ))}
            </div>
          </section>
        ) : (
          <div className="rounded-card border border-dashed border-ink-200 bg-white/70 px-6 py-14 text-center">
            <span className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-ink-100 text-ink-500">
              <AlertCircle className="size-6" aria-hidden="true" />
            </span>
            <h2 className="mt-4 text-base font-semibold text-ink-800">Sin módulos asignados</h2>
            <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-ink-500">
              Tu usuario no tiene módulos habilitados con el plan actual. Solicita el acceso al
              administrador del sistema.
            </p>
          </div>
        )
      ) : null}

      {!isPending && categorias.length > 0 ? (
        <p className="flex items-center justify-center gap-2 pt-2 text-xs text-ink-400">
          <Sparkles className="size-3.5" aria-hidden="true" />
          Los módulos marcados como “Nuevo panel” ya funcionan dentro de EDSuite Web.
        </p>
      ) : null}
    </div>
  );
}
